import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";
import { useFleetData } from "./FleetDataContext";
import Nav from "./Nav";

const ACCENT = "#fcd657";
const TYPE_COLORS = {
  Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa",
  Mining: "#fcd657", Trade: "#4fc3f7", Exploration: "#4fc3f7",
  Recovery: "#a78bfa", Social: "#ce93d8", Training: "#34d399",
};

/**
 * Ops Planner — modular timeline / op-graph for a single event.
 *
 * Routes: /events/:eventId/ops-planner
 *
 * Data model (see ops_planner_schema migration):
 *   ops_plans         — one per event
 *   ops_plan_nodes    — positioned boxes (phases) on the canvas; .sections jsonb
 *                       holds sub-cards (each with own title/groups/crew/ships)
 *   ops_plan_edges    — directed connections (from_node → to_node)
 *   ops_plan_attachments — files attached to a node or to the plan as a whole
 *
 * Features:
 *   - Add / drag / resize / delete phase nodes
 *   - Connect-mode draws directed edges between nodes
 *   - Canvas zoom (50/75/100/125/150/200 %) and natural pan via scroll
 *   - Rich node editor in a side panel: title, body, groups, crew, ships,
 *     and sub-sections (each with its own pickers)
 *   - Visual avatars (crew) and ship thumbnails on the node face
 */
export default function OpsPlannerPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { profiles: allProfiles, ships: allShips } = useFleetData();

  const [event, setEvent] = useState(null);
  const [plan, setPlan] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [groupShips, setGroupShips] = useState([]); // event_group_ships rows
  const [flagships, setFlagships] = useState([]);   // event_flagships rows
  const [attachments, setAttachments] = useState([]); // ops_plan_attachments rows
  const [loading, setLoading] = useState(true);
  const [connectingFrom, setConnectingFrom] = useState(null); // node id, or "__waiting__" to mean "choose source"
  const [zoom, setZoom] = useState(1);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState(() => new Set());
  const [comms, setComms] = useState([]); // ops_comms_assignments rows
  const [commsOpen, setCommsOpen] = useState(false);
  // Ref mirror of `nodes` so async drag callbacks see the latest positions.
  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  // Holds a snapshot of starting positions at drag start so multi-drag can
  // apply the same delta to every selected node without re-reading state.
  const dragSnapshotRef = useRef(null);

  // ─── Load event + plan ──────────────────────────────────────────────────
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const { data: ev } = await supabase.from("events").select("*").eq("id", eventId).single();
      if (cancel) return;
      setEvent(ev || null);

      let { data: planRow } = await supabase.from("ops_plans").select("*").eq("event_id", eventId).maybeSingle();
      if (!planRow && ev) {
        const { data: created } = await supabase.from("ops_plans").insert([{
          event_id: eventId, created_by: profile?.id || null,
        }]).select().single();
        planRow = created;
      }
      if (cancel) return;
      setPlan(planRow || null);

      if (planRow) {
        const [{ data: ns }, { data: es }, { data: gs }, { data: fs }, { data: ats }, { data: cs }] = await Promise.all([
          supabase.from("ops_plan_nodes").select("*").eq("plan_id", planRow.id).order("sort_order"),
          supabase.from("ops_plan_edges").select("*").eq("plan_id", planRow.id),
          supabase.from("event_group_ships").select("ship_id, group_name, quantity_requested").eq("event_id", eventId),
          supabase.from("event_flagships").select("ship_id").eq("event_id", eventId),
          supabase.from("ops_plan_attachments").select("*").eq("plan_id", planRow.id).order("uploaded_at"),
          supabase.from("ops_comms_assignments").select("*").eq("plan_id", planRow.id).order("sort_order"),
        ]);
        if (!cancel) {
          setNodes(ns || []);
          setEdges(es || []);
          setGroupShips(gs || []);
          setFlagships(fs || []);
          setAttachments(ats || []);
          setComms(cs || []);
        }
      }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, [eventId, profile?.id]);

  const color = event ? (TYPE_COLORS[event.type] || ACCENT) : ACCENT;
  const editingNode = nodes.find(n => n.id === editingNodeId) || null;

  // Available pickers data — keyed/filtered to what the user can attach.
  // Groups: union of event.groups[] + any group names attendees are assigned to.
  const eventAttendees = useMemo(() => {
    if (!event) return [];
    const raw = Array.isArray(event.attendees) ? event.attendees : [];
    return raw.map(a => typeof a === "string" ? { user_id: a } : a).filter(a => a && a.user_id);
  }, [event]);

  const availableGroups = useMemo(() => {
    if (!event) return [];
    const set = new Set();
    (Array.isArray(event.groups) ? event.groups : []).forEach(g => g?.name && set.add(g.name));
    eventAttendees.forEach(a => a.group && set.add(a.group));
    return [...set].sort();
  }, [event, eventAttendees]);

  const attendeesWithProfile = useMemo(() => {
    return eventAttendees.map(a => {
      const p = (allProfiles || []).find(p => p.id === a.user_id);
      return {
        user_id: a.user_id,
        username: a.username || p?.discord_username || p?.discord_handle || "Unknown",
        avatar: a.avatar || p?.discord_avatar || null,
        group: a.group || null,
      };
    });
  }, [eventAttendees, allProfiles]);

  // Map of group_name -> [ship objects requested for that group] plus a flat
  // flagship list. These feed the per-node ship picker so users can only pick
  // ships that have actually been assigned to one of the linked groups.
  const shipsByGroup = useMemo(() => {
    const m = {};
    for (const gs of groupShips) {
      const ship = (allShips || []).find(s => s.id === gs.ship_id);
      if (!ship) continue;
      if (!m[gs.group_name]) m[gs.group_name] = [];
      if (!m[gs.group_name].some(s => s.id === ship.id)) m[gs.group_name].push(ship);
    }
    return m;
  }, [groupShips, allShips]);

  const flagshipObjs = useMemo(() => {
    return flagships.map(f => (allShips || []).find(s => s.id === f.ship_id)).filter(Boolean);
  }, [flagships, allShips]);

  // ─── Node CRUD ──────────────────────────────────────────────────────────
  async function addNode() {
    if (!plan) return;
    const offset = nodes.length;
    const newRow = {
      plan_id: plan.id,
      title: "New Phase",
      body: "",
      node_type: "phase",
      position_x: 80 + (offset % 4) * 300,
      position_y: 80 + Math.floor(offset / 4) * 240,
      width: 260,
      height: 180,
      sort_order: offset,
      created_by: profile?.id || null,
    };
    const { data, error } = await supabase.from("ops_plan_nodes").insert([newRow]).select().single();
    if (error) { console.error("[ops] addNode failed", error); return; }
    setNodes(prev => [...prev, data]);
  }

  async function updateNode(id, patch) {
    setNodes(prev => prev.map(n => n.id === id ? { ...n, ...patch } : n));
    const { error } = await supabase.from("ops_plan_nodes").update(patch).eq("id", id);
    if (error) console.error("[ops] updateNode failed", error);
  }

  // Multi-drag: when the user drags one phase, every selected phase moves by
  // the same delta. We snapshot starting positions once at drag start, apply
  // local-state deltas during the drag (no DB writes per tick), then persist
  // the final positions in one batch when the gesture ends.
  function dragDelta(dragId, dx, dy) {
    if (!dragSnapshotRef.current) {
      // Choose which set of nodes moves. If the dragged node is part of an
      // active selection, the whole selection moves; otherwise just this one.
      const ids = selectedNodeIds.has(dragId)
        ? Array.from(selectedNodeIds)
        : [dragId];
      const snap = new Map();
      for (const id of ids) {
        const n = nodesRef.current.find(x => x.id === id);
        if (n) snap.set(id, { x: n.position_x, y: n.position_y });
      }
      dragSnapshotRef.current = { ids, snap };
    }
    const { ids, snap } = dragSnapshotRef.current;
    const idSet = new Set(ids);
    setNodes(prev => prev.map(n => {
      if (!idSet.has(n.id)) return n;
      const orig = snap.get(n.id);
      return { ...n, position_x: Math.max(0, orig.x + dx), position_y: Math.max(0, orig.y + dy) };
    }));
  }
  async function dragEnd() {
    const ref = dragSnapshotRef.current;
    dragSnapshotRef.current = null;
    if (!ref) return;
    const current = nodesRef.current;
    for (const id of ref.ids) {
      const n = current.find(x => x.id === id);
      if (!n) continue;
      const orig = ref.snap.get(id);
      if (orig && orig.x === n.position_x && orig.y === n.position_y) continue; // no real change
      const { error } = await supabase.from("ops_plan_nodes")
        .update({ position_x: n.position_x, position_y: n.position_y }).eq("id", id);
      if (error) console.error("[ops] dragEnd persist failed", error);
    }
  }

  function toggleNodeSelection(id, additive) {
    setSelectedNodeIds(prev => {
      const next = new Set(prev);
      if (additive) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  }

  async function deleteNode(id) {
    if (!window.confirm("Remove this phase from the plan? Connected edges go with it.")) return;
    setNodes(prev => prev.filter(n => n.id !== id));
    setEdges(prev => prev.filter(e => e.from_node_id !== id && e.to_node_id !== id));
    const { error } = await supabase.from("ops_plan_nodes").delete().eq("id", id);
    if (error) console.error("[ops] deleteNode failed", error);
    if (editingNodeId === id) setEditingNodeId(null);
  }

  // ─── Edge CRUD ──────────────────────────────────────────────────────────
  async function addEdge(fromId, toId) {
    if (!plan || fromId === toId) return;
    if (edges.some(e => e.from_node_id === fromId && e.to_node_id === toId)) return;
    const { data, error } = await supabase.from("ops_plan_edges").insert([{
      plan_id: plan.id, from_node_id: fromId, to_node_id: toId, routing: "straight",
    }]).select().single();
    if (error) { console.error("[ops] addEdge failed", error); return; }
    setEdges(prev => [...prev, data]);
  }

  async function deleteEdge(id) {
    setEdges(prev => prev.filter(e => e.id !== id));
    if (selectedEdgeId === id) setSelectedEdgeId(null);
    const { error } = await supabase.from("ops_plan_edges").delete().eq("id", id);
    if (error) console.error("[ops] deleteEdge failed", error);
  }

  async function updateEdge(id, patch) {
    setEdges(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
    const { error } = await supabase.from("ops_plan_edges").update(patch).eq("id", id);
    if (error) console.error("[ops] updateEdge failed", error);
  }

  // ─── Attachments ────────────────────────────────────────────────────────
  // Files uploaded to the ops-plan-files Supabase Storage bucket. node_id can
  // be null (plan-level) and section_id (text) can be null (node-level or
  // plan-level). Path layout: <plan_id>/<node_id or "plan">/<section_id or "block">/<timestamp>-<name>
  async function uploadAttachment({ file, nodeId = null, sectionId = null }) {
    if (!plan || !file) return null;
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${plan.id}/${nodeId || "plan"}/${sectionId || "block"}/${Date.now()}-${safeName}`;
    const { error: upErr } = await supabase.storage.from("ops-plan-files").upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) { console.error("[ops] upload failed", upErr); return null; }
    const { data: urlData } = supabase.storage.from("ops-plan-files").getPublicUrl(path);
    const row = {
      plan_id: plan.id,
      node_id: nodeId,
      section_id: sectionId,
      filename: file.name,
      file_url: urlData?.publicUrl || "",
      file_type: file.type || null,
      uploaded_by: profile?.id || null,
    };
    const { data, error } = await supabase.from("ops_plan_attachments").insert([row]).select().single();
    if (error) { console.error("[ops] attachment insert failed", error); return null; }
    setAttachments(prev => [...prev, data]);
    return data;
  }
  async function addComms({ mode, groupName = null, userId = null, channel = "" }) {
    if (!plan) return;
    const row = {
      plan_id: plan.id,
      mode,
      group_name: mode === "vc" ? (groupName || "") : null,
      user_id: mode === "radio" ? userId : null,
      channel,
      sort_order: comms.filter(c => c.mode === mode).length,
      created_by: profile?.id || null,
    };
    const { data, error } = await supabase.from("ops_comms_assignments").insert([row]).select().single();
    if (error) { console.error("[ops] addComms failed", error); return null; }
    setComms(prev => [...prev, data]);
    return data;
  }
  async function updateComms(id, patch) {
    setComms(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    const { error } = await supabase.from("ops_comms_assignments").update(patch).eq("id", id);
    if (error) console.error("[ops] updateComms failed", error);
  }
  async function deleteComms(id) {
    setComms(prev => prev.filter(c => c.id !== id));
    const { error } = await supabase.from("ops_comms_assignments").delete().eq("id", id);
    if (error) console.error("[ops] deleteComms failed", error);
  }

  async function deleteAttachment(id) {
    const att = attachments.find(a => a.id === id);
    setAttachments(prev => prev.filter(a => a.id !== id));
    const { error } = await supabase.from("ops_plan_attachments").delete().eq("id", id);
    if (error) console.error("[ops] attachment delete failed", error);
    // Best-effort cleanup of the bucket object — file_url has the path after
    // /ops-plan-files/. Failure here doesn't roll back the row delete.
    if (att?.file_url) {
      const m = att.file_url.match(/\/ops-plan-files\/(.+)$/);
      if (m) await supabase.storage.from("ops-plan-files").remove([m[1]]);
    }
  }

  function handleNodeConnectClick(nodeId) {
    if (!connectingFrom || connectingFrom === "__waiting__") { setConnectingFrom(nodeId); return; }
    if (connectingFrom === nodeId) { setConnectingFrom(null); return; }
    addEdge(connectingFrom, nodeId);
    setConnectingFrom(null);
  }

  if (loading) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", color: ACCENT, fontFamily: "'Orbitron', sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
        LOADING OPS PLAN…
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", color: "#e8eaf6", padding: 40 }}>
        <Nav />
        <p style={{ fontFamily: "'Rajdhani', sans-serif", marginTop: 80 }}>Event not found.</p>
      </div>
    );
  }

  return (
    <div style={{ background: "#05070f", minHeight: "100vh", color: "#e8eaf6", position: "relative" }}>
      <style>{`
        nav a, nav a:visited, nav a:hover, nav a:active { text-decoration: none !important; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; text-decoration: none; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: ${color}; transition: width 0.3s ease; }
        .nav-link:hover { color: ${color}; }
        .nav-link:hover::after { width: 100%; }
        /* Apply the same growing-underline hover treatment to nav entries that
           don't carry the .nav-link class (TSR team link, ADMIN link, etc.).
           Uses currentColor so the underline picks up whatever the link's text
           color is — team colour for TSR, gold for ADMIN. */
        nav a:not(.nav-link):not([title="View your profile"]) { position: relative; }
        nav a:not(.nav-link):not([title="View your profile"])::after { content: ''; position: absolute; bottom: -2px; left: 0; width: 0; height: 1px; background: currentColor; transition: width 0.3s ease; opacity: 0.85; }
        nav a:not(.nav-link):not([title="View your profile"]):hover::after { width: 100%; }

        /* Mobile portrait — give the planner room to breathe on a phone. */
        @media (max-width: 768px) {
          .ops-header { padding: 90px 12px 12px !important; }
          .ops-header h1 { font-size: 18px !important; }
          .ops-toolbar { width: 100%; justify-content: flex-start !important; gap: 6px !important; }
          .ops-toolbar button { font-size: 9px !important; padding: 7px 10px !important; }
          .ops-canvas-pad { padding: 8px !important; }
          /* Buttons inside the per-edge toolbar shrink so the popover fits. */
          .ops-edge-toolbar { flex-wrap: wrap; max-width: 86vw; }
        }
      `}</style>
      <Nav />

      <header className="ops-header" style={{ padding: "112px clamp(16px, 4vw, 40px) 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", position: "relative", zIndex: 2 }}>
        <button onClick={() => navigate(`/events/${eventId}/review`)}
          style={{ background: "none", border: "none", color: "rgba(232,234,246,0.5)", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", padding: 0, marginBottom: 12 }}>
          ← Back to event
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color, textTransform: "uppercase", marginBottom: 6 }}>Ops Planner</div>
              <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 4vw, 32px)", margin: 0 }}>{event.title}</h1>
            </div>
            <button onClick={() => setCommsOpen(true)} type="button"
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, padding: "8px 14px", background: `${color}14`, border: `1px solid ${color}77`, color, textTransform: "uppercase", cursor: "pointer" }}>
              ⌬ Comms Assignments
            </button>
          </div>
          <div className="ops-toolbar" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <ZoomControls zoom={zoom} setZoom={setZoom} color={color} />
            <button onClick={addNode}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, padding: "8px 14px", background: `${color}18`, border: `1px solid ${color}`, color, textTransform: "uppercase", cursor: "pointer" }}>
              + Add Phase
            </button>
            <button onClick={() => setConnectingFrom(connectingFrom ? null : "__waiting__")}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, padding: "8px 14px", background: connectingFrom ? "rgba(79,195,247,0.18)" : "transparent", border: `1px solid ${connectingFrom ? "#4fc3f7" : "rgba(255,255,255,0.15)"}`, color: connectingFrom ? "#4fc3f7" : "rgba(232,234,246,0.5)", textTransform: "uppercase", cursor: "pointer" }}>
              {connectingFrom ? "Connecting…" : "Connect Phases"}
            </button>
          </div>
        </div>
        {connectingFrom && (
          <div style={{ marginTop: 12, fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#4fc3f7", letterSpacing: 1 }}>
            {connectingFrom === "__waiting__"
              ? "Click the first phase to connect from."
              : "Click the destination phase. Click the same phase again to cancel."}
          </div>
        )}
      </header>

      <div className="ops-canvas-pad" style={{ padding: 24, position: "relative", minHeight: "calc(100vh - 220px)", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        {nodes.length === 0 ? (
          <div style={{ textAlign: "center", padding: 80, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase", border: "1px dashed rgba(255,255,255,0.1)" }}>
            No phases yet. Click "+ Add Phase" to start mapping the operation.
          </div>
        ) : (
          <PlannerCanvas
            nodes={nodes}
            edges={edges}
            color={color}
            zoom={zoom}
            connectingFrom={connectingFrom === "__waiting__" ? null : connectingFrom}
            isConnectMode={!!connectingFrom}
            attendeesWithProfile={attendeesWithProfile}
            allShips={allShips || []}
            shipsByGroup={shipsByGroup}
            flagshipObjs={flagshipObjs}
            attachments={attachments}
            onConnectClick={handleNodeConnectClick}
            onNodeUpdate={updateNode}
            onNodeDelete={deleteNode}
            onNodeEdit={(id) => setEditingNodeId(id)}
            onEdgeDelete={deleteEdge}
            onEdgeUpdate={updateEdge}
            selectedEdgeId={selectedEdgeId}
            onEdgeSelect={setSelectedEdgeId}
            selectedNodeIds={selectedNodeIds}
            setSelectedNodeIds={setSelectedNodeIds}
            onToggleNodeSelection={toggleNodeSelection}
            onDragDelta={dragDelta}
            onDragEnd={dragEnd}
          />
        )}
      </div>

      {commsOpen && (
        <CommsAssignmentsPanel
          color={color}
          comms={comms}
          groups={availableGroups}
          attendees={attendeesWithProfile}
          onAdd={addComms}
          onUpdate={updateComms}
          onDelete={deleteComms}
          onClose={() => setCommsOpen(false)}
        />
      )}

      {editingNode && (
        <NodeEditorPanel
          node={editingNode}
          color={color}
          groups={availableGroups}
          attendees={attendeesWithProfile}
          ships={allShips || []}
          shipsByGroup={shipsByGroup}
          flagshipObjs={flagshipObjs}
          attachments={attachments}
          onUpload={uploadAttachment}
          onDeleteAttachment={deleteAttachment}
          onClose={() => setEditingNodeId(null)}
          onChange={(patch) => updateNode(editingNode.id, patch)}
        />
      )}
    </div>
  );
}

// ─── Header zoom controls ─────────────────────────────────────────────────
function ZoomControls({ zoom, setZoom, color }) {
  const presets = [0.5, 0.75, 1, 1.25, 1.5, 2];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 6px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
      <button onClick={() => setZoom(z => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))}
        style={{ background: "transparent", border: "none", color: "rgba(232,234,246,0.6)", fontSize: 14, lineHeight: 1, padding: "4px 6px", cursor: "pointer" }} title="Zoom out">−</button>
      <select value={zoom} onChange={e => setZoom(parseFloat(e.target.value))}
        style={{ background: "#05070f", border: `1px solid ${color}33`, color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 10, padding: "3px 6px", cursor: "pointer" }}>
        {presets.map(z => <option key={z} value={z}>{Math.round(z * 100)}%</option>)}
      </select>
      <button onClick={() => setZoom(z => Math.min(3, Math.round((z + 0.1) * 100) / 100))}
        style={{ background: "transparent", border: "none", color: "rgba(232,234,246,0.6)", fontSize: 14, lineHeight: 1, padding: "4px 6px", cursor: "pointer" }} title="Zoom in">+</button>
    </div>
  );
}

// ─── Canvas + Node + Edge rendering ───────────────────────────────────────
function PlannerCanvas({ nodes, edges, color, zoom, connectingFrom, isConnectMode, attendeesWithProfile, allShips, shipsByGroup, flagshipObjs, attachments = [], onConnectClick, onNodeUpdate, onNodeDelete, onNodeEdit, onEdgeDelete, onEdgeUpdate, selectedEdgeId, onEdgeSelect, selectedNodeIds, setSelectedNodeIds, onToggleNodeSelection, onDragDelta, onDragEnd }) {
  // Marquee box-selection state. Stored in unscaled canvas coords so the
  // intersection math matches node.position_*. Mousedown on the empty canvas
  // (target === inner canvas div) starts a drag; mouseup commits selection.
  const canvasInnerRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const marqueeRef = useRef(null);
  marqueeRef.current = marquee;

  function startMarquee(e) {
    // Only when the click landed on the canvas background, not on a node, an
    // SVG edge, or any other interactive child.
    if (e.target !== canvasInnerRef.current && e.target !== e.currentTarget) return;
    if (e.button !== 0) return;
    const rect = canvasInnerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    setMarquee({ startX: x, startY: y, currentX: x, currentY: y });
    document.addEventListener("mousemove", moveMarquee);
    document.addEventListener("mouseup", endMarquee, { once: true });
  }
  function moveMarquee(e) {
    if (!marqueeRef.current || !canvasInnerRef.current) return;
    const rect = canvasInnerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    setMarquee(m => m ? { ...m, currentX: x, currentY: y } : m);
  }
  function endMarquee() {
    document.removeEventListener("mousemove", moveMarquee);
    const m = marqueeRef.current;
    setMarquee(null);
    if (!m) return;
    const x1 = Math.min(m.startX, m.currentX);
    const y1 = Math.min(m.startY, m.currentY);
    const x2 = Math.max(m.startX, m.currentX);
    const y2 = Math.max(m.startY, m.currentY);
    const dx = x2 - x1, dy = y2 - y1;
    // A real marquee needs at least a few pixels of drag — otherwise treat as
    // a deselect-on-empty-click.
    if (dx < 4 && dy < 4) {
      if (setSelectedNodeIds) setSelectedNodeIds(new Set());
      return;
    }
    const inside = new Set();
    for (const n of nodes) {
      const nx1 = n.position_x;
      const ny1 = n.position_y;
      const nx2 = nx1 + (n.width || 260);
      const ny2 = ny1 + (n.height || 180);
      // Intersect (any overlap counts as selected).
      if (nx1 < x2 && nx2 > x1 && ny1 < y2 && ny2 > y1) inside.add(n.id);
    }
    if (setSelectedNodeIds) setSelectedNodeIds(inside);
  }

  // Bounding box so the canvas grows with content (in unscaled coords).
  const bounds = useMemo(() => {
    const maxX = Math.max(800, ...nodes.map(n => n.position_x + (n.width || 260) + 60));
    const maxY = Math.max(500, ...nodes.map(n => n.position_y + (n.height || 180) + 60));
    return { maxX, maxY };
  }, [nodes]);

  // ─── Anchor math ────────────────────────────────────────────────────────
  // Each phase has 8 anchor dots; we pick the pair that sits on the side of
  // each box facing the other one so the line clearly "comes from" and "goes
  // to" a specific anchor instead of disappearing into the box centre.
  function anchorPoint(node, key) {
    const x = node.position_x, y = node.position_y;
    const w = node.width || 260, h = node.height || 180;
    switch (key) {
      case "tl": return [x, y];
      case "tc": return [x + w / 2, y];
      case "tr": return [x + w, y];
      case "rc": return [x + w, y + h / 2];
      case "br": return [x + w, y + h];
      case "bc": return [x + w / 2, y + h];
      case "bl": return [x, y + h];
      case "lc": return [x, y + h / 2];
      default:   return [x + w / 2, y + h / 2];
    }
  }
  function pickAnchors(from, to) {
    const fcx = from.position_x + (from.width || 260) / 2;
    const fcy = from.position_y + (from.height || 180) / 2;
    const tcx = to.position_x + (to.width || 260) / 2;
    const tcy = to.position_y + (to.height || 180) / 2;
    const dx = tcx - fcx, dy = tcy - fcy;
    if (Math.abs(dx) >= Math.abs(dy)) {
      return dx > 0 ? { from: "rc", to: "lc" } : { from: "lc", to: "rc" };
    }
    return dy > 0 ? { from: "bc", to: "tc" } : { from: "tc", to: "bc" };
  }
  // Push the endpoint outward (away from the box centre) by the dot radius so
  // the arrow visibly terminates at the dot's outer edge instead of being
  // covered by the dot itself.
  const DOT_R = 9; // a hair past the 8px dot radius for clean termination
  function anchorShift(key) {
    const d = DOT_R / Math.SQRT2;
    switch (key) {
      case "tl": return [-d, -d];
      case "tc": return [0, -DOT_R];
      case "tr": return [d, -d];
      case "rc": return [DOT_R, 0];
      case "br": return [d, d];
      case "bc": return [0, DOT_R];
      case "bl": return [-d, d];
      case "lc": return [-DOT_R, 0];
      default:   return [0, 0];
    }
  }
  function anchorEndpoint(node, key) {
    const [bx, by] = anchorPoint(node, key);
    const [sx, sy] = anchorShift(key);
    return [bx + sx, by + sy];
  }

  // Outer wrapper takes the SCALED dimensions so scroll math is right.
  return (
    <div style={{
      position: "relative",
      width: bounds.maxX * zoom,
      height: bounds.maxY * zoom,
      overflow: "visible",
      flexShrink: 0,
    }}>
      <div style={{
        position: "absolute",
        top: 0, left: 0,
        width: bounds.maxX,
        height: bounds.maxY,
        background: "rgba(255,255,255,0.015)",
        border: "1px solid rgba(255,255,255,0.05)",
        transform: `scale(${zoom})`,
        transformOrigin: "top left",
      }}>
        <svg width={bounds.maxX} height={bounds.maxY}
          onClick={() => onEdgeSelect && onEdgeSelect(null)}
          style={{ position: "absolute", top: 0, left: 0 }}>
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
          </defs>
          {/* Background layer — clicks pass through to nodes EXCEPT on edge hit
              targets. We split each edge into a wide invisible click strip plus
              the visible stroke so clicking the line selects it. */}
          {edges.map(e => {
            const from = nodes.find(n => n.id === e.from_node_id);
            const to = nodes.find(n => n.id === e.to_node_id);
            if (!from || !to) return null;
            const picked = pickAnchors(from, to);
            const [fx, fy] = anchorEndpoint(from, picked.from);
            const [tx, ty] = anchorEndpoint(to, picked.to);
            const selected = selectedEdgeId === e.id;
            const dash = e.style === "dashed" ? "6 4" : e.style === "dotted" ? "2 4" : "0";

            // Orthogonal = Z-shape (two right-angle bends). Source exits
            // perpendicular to its edge, line crosses at the midpoint, target
            // arrives perpendicular to its edge. If the two anchors happen to
            // already align on the bend axis, the polyline collapses to a
            // straight line naturally.
            let pathD;
            let midX, midY;
            const sourceIsHorizEdge = picked.from === "lc" || picked.from === "rc";
            if (e.routing === "orthogonal") {
              if (sourceIsHorizEdge) {
                const cx = (fx + tx) / 2;
                pathD = `M ${fx} ${fy} L ${cx} ${fy} L ${cx} ${ty} L ${tx} ${ty}`;
                midX = cx; midY = (fy + ty) / 2;
              } else {
                const cy = (fy + ty) / 2;
                pathD = `M ${fx} ${fy} L ${fx} ${cy} L ${tx} ${cy} L ${tx} ${ty}`;
                midX = (fx + tx) / 2; midY = cy;
              }
            } else {
              pathD = `M ${fx} ${fy} L ${tx} ${ty}`;
              midX = (fx + tx) / 2; midY = (fy + ty) / 2;
            }
            return (
              <g key={e.id} style={{ cursor: "pointer", pointerEvents: "auto" }}
                onClick={(ev) => { ev.stopPropagation(); onEdgeSelect && onEdgeSelect(e.id); }}>
                {/* Invisible thick click target */}
                <path d={pathD} fill="none" stroke="transparent" strokeWidth="18" />
                {/* Visible stroke */}
                <path d={pathD} fill="none"
                  stroke={selected ? "#4fc3f7" : color}
                  strokeWidth={selected ? "3" : "2"}
                  strokeOpacity={selected ? 0.9 : 0.55}
                  strokeDasharray={dash}
                  markerEnd="url(#arrow)" />
                {e.label && (
                  <text x={midX} y={midY - 6} textAnchor="middle"
                    fill="#e8eaf6" fontFamily="'Space Mono', monospace" fontSize="10" opacity="0.75"
                    style={{ pointerEvents: "none" }}>{e.label}</text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Floating per-edge toolbar — appears next to the midpoint of the
            currently-selected edge. Lets the user toggle routing, line style,
            label, and delete in place. */}
        {selectedEdgeId && (() => {
          const e = edges.find(x => x.id === selectedEdgeId);
          if (!e) return null;
          const from = nodes.find(n => n.id === e.from_node_id);
          const to = nodes.find(n => n.id === e.to_node_id);
          if (!from || !to) return null;
          const picked = pickAnchors(from, to);
          const [fx, fy] = anchorEndpoint(from, picked.from);
          const [tx, ty] = anchorEndpoint(to, picked.to);
          const sourceIsHorizEdge = picked.from === "lc" || picked.from === "rc";
          let mx, my;
          if (e.routing === "orthogonal") {
            // Z-shape midpoint = the middle of the central perpendicular segment.
            mx = sourceIsHorizEdge ? (fx + tx) / 2 : (fx + tx) / 2;
            my = sourceIsHorizEdge ? (fy + ty) / 2 : (fy + ty) / 2;
          } else {
            mx = (fx + tx) / 2; my = (fy + ty) / 2;
          }
          return (
            <div className="ops-edge-toolbar" onClick={ev => ev.stopPropagation()}
              style={{ position: "absolute", left: mx, top: my, transform: "translate(-50%, -50%)", padding: "6px 8px", background: "#0a0d18", border: "1px solid rgba(79,195,247,0.55)", boxShadow: "0 0 12px rgba(79,195,247,0.25)", display: "flex", alignItems: "center", gap: 6, zIndex: 5 }}>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", letterSpacing: 1.5, textTransform: "uppercase" }}>Path</span>
              <button onClick={() => onEdgeUpdate(e.id, { routing: "straight" })} type="button"
                style={{ padding: "2px 8px", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, background: e.routing !== "orthogonal" ? `${color}22` : "transparent", border: `1px solid ${e.routing !== "orthogonal" ? color : "rgba(255,255,255,0.15)"}`, color: e.routing !== "orthogonal" ? color : "rgba(232,234,246,0.55)", textTransform: "uppercase", cursor: "pointer" }}>↗ Straight</button>
              <button onClick={() => onEdgeUpdate(e.id, { routing: "orthogonal" })} type="button"
                style={{ padding: "2px 8px", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, background: e.routing === "orthogonal" ? `${color}22` : "transparent", border: `1px solid ${e.routing === "orthogonal" ? color : "rgba(255,255,255,0.15)"}`, color: e.routing === "orthogonal" ? color : "rgba(232,234,246,0.55)", textTransform: "uppercase", cursor: "pointer" }}>┐ 90°</button>
              <span style={{ width: 1, height: 16, background: "rgba(255,255,255,0.1)", margin: "0 2px" }} />
              <select value={e.style || "solid"} onChange={ev => onEdgeUpdate(e.id, { style: ev.target.value })}
                style={{ background: "#05070f", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, padding: "2px 4px", cursor: "pointer" }}>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
              <input value={e.label || ""} onChange={ev => onEdgeUpdate(e.id, { label: ev.target.value })} placeholder="Label"
                style={{ width: 100, padding: "2px 6px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 11 }} />
              <button onClick={() => onEdgeDelete(e.id)} title="Delete edge" type="button"
                style={{ padding: "2px 8px", background: "transparent", border: "1px solid rgba(239,83,80,0.4)", color: "#ef5350", fontSize: 11, cursor: "pointer" }}>×</button>
            </div>
          );
        })()}

        {nodes.map(n => (
          <PlannerNode
            key={n.id}
            node={n}
            color={color}
            zoom={zoom}
            attendeesWithProfile={attendeesWithProfile}
            allShips={allShips}
            attachments={attachments}
            isConnectSource={connectingFrom === n.id}
            isConnectMode={isConnectMode}
            onConnectClick={() => onConnectClick(n.id)}
            onUpdate={(patch) => onNodeUpdate(n.id, patch)}
            onDelete={() => onNodeDelete(n.id)}
            onEdit={() => onNodeEdit(n.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PlannerNode({ node, color, zoom, attendeesWithProfile, allShips, attachments = [], isConnectSource, isConnectMode, onConnectClick, onUpdate, onDelete, onEdit }) {
  // Drag-to-move. Translate raw cursor delta into canvas-space delta by /zoom.
  const dragRef = useRef(null);
  function startDrag(e) {
    // Don't intercept clicks on Edit/×/anchor-dots/resize-handle (those carry
    // data-no-drag) but ALWAYS start tracking so we can decide click-vs-drag
    // on mouseup. A mouseup with no real movement counts as a connect click.
    if (e.target.closest("[data-no-drag]")) return;
    const point = (e.touches && e.touches[0]) || e;
    if (!e.touches) e.preventDefault();
    dragRef.current = { startX: point.clientX, startY: point.clientY, origX: node.position_x, origY: node.position_y, mode: "move", moved: false };
    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup", endDrag, { once: true });
    document.addEventListener("touchmove", onDragMove, { passive: false });
    document.addEventListener("touchend", endDrag, { once: true });
    document.addEventListener("touchcancel", endDrag, { once: true });
  }
  function startResize(e) {
    const point = (e.touches && e.touches[0]) || e;
    if (!e.touches) e.preventDefault();
    e.stopPropagation();
    dragRef.current = { startX: point.clientX, startY: point.clientY, origW: node.width || 260, origH: node.height || 180, mode: "resize" };
    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup", endDrag, { once: true });
    document.addEventListener("touchmove", onDragMove, { passive: false });
    document.addEventListener("touchend", endDrag, { once: true });
    document.addEventListener("touchcancel", endDrag, { once: true });
  }
  function onDragMove(e) {
    if (!dragRef.current) return;
    const point = (e.touches && e.touches[0]) || e;
    if (e.touches && dragRef.current.moved) e.preventDefault();  // stop page scroll mid-drag
    const dx = (point.clientX - dragRef.current.startX) / zoom;
    const dy = (point.clientY - dragRef.current.startY) / zoom;
    // Threshold so a tiny mouse twitch on click doesn't count as a drag.
    if (!dragRef.current.moved && dragRef.current.mode === "move" && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    dragRef.current.moved = true;
    if (dragRef.current.mode === "move") {
      onUpdate({ position_x: Math.max(0, dragRef.current.origX + dx), position_y: Math.max(0, dragRef.current.origY + dy) });
    } else if (dragRef.current.mode === "resize") {
      onUpdate({
        width: Math.max(180, Math.round(dragRef.current.origW + dx)),
        height: Math.max(120, Math.round(dragRef.current.origH + dy)),
      });
    }
  }
  function endDrag() {
    const ref = dragRef.current;
    dragRef.current = null;
    document.removeEventListener("mousemove", onDragMove);
    document.removeEventListener("touchmove", onDragMove);
    // Treat a mouseup with no real movement as a click. Only initiates a
    // connection when Connect Phases mode is explicitly toggled on — outside
    // that mode, a plain click on the card body does nothing. Resize never
    // counts as a click.
    if (ref && !ref.moved && ref.mode === "move" && isConnectMode && onConnectClick) {
      onConnectClick();
    }
  }

  const width = node.width || 260;
  const height = node.height || 180;
  // Build the combined avatar list: explicitly-linked attendees first, then any
  // members of node.group_names if `show_group_members` is on. Each entry carries
  // a `fromGroup` flag so we can style group-derived avatars differently.
  const explicitIds = new Set(node.attendee_user_ids || []);
  const explicit = (node.attendee_user_ids || []).map(uid => {
    const a = attendeesWithProfile.find(p => p.user_id === uid);
    return a ? { ...a, fromGroup: false } : null;
  }).filter(Boolean);
  const groupSet = new Set(node.group_names || []);
  const fromGroup = node.show_group_members
    ? attendeesWithProfile.filter(a => a.group && groupSet.has(a.group) && !explicitIds.has(a.user_id)).map(a => ({ ...a, fromGroup: true }))
    : [];
  const linkedAttendees = [...explicit, ...fromGroup];
  const linkedShips = (node.ship_ids || []).map(sid => allShips.find(s => s.id === sid)).filter(Boolean);
  const sections = Array.isArray(node.sections) ? node.sections : [];

  return (
    <div
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      style={{
        position: "absolute",
        left: node.position_x, top: node.position_y,
        width, minHeight: height,
        background: "#0a0d18",
        border: `2px solid ${isConnectSource ? "#4fc3f7" : `${color}77`}`,
        boxShadow: isConnectSource ? "0 0 16px rgba(79,195,247,0.4)" : `0 0 8px ${color}22`,
        cursor: isConnectMode || isConnectSource ? "crosshair" : "move",
        padding: 10,
        userSelect: "none",
        overflow: "hidden",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}>
      <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 12, color: "#e8eaf6", marginBottom: 6, wordBreak: "break-word", paddingRight: 60 }}>{node.title}</div>

      {/* Block-level chips — surfaced at the top so groups + crew read like a header */}
      {(node.group_names?.length > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {(node.group_names || []).map(g => (
            <span key={g} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "2px 6px", background: `${color}18`, border: `1px solid ${color}55`, color: `${color}cc`, textTransform: "uppercase" }}>{g}</span>
          ))}
        </div>
      )}

      {/* Crew avatars */}
      {linkedAttendees.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, marginBottom: 6 }}>
          {linkedAttendees.slice(0, 8).map((a, i) => {
            // Solid border = explicitly linked. Dashed border = from a group expand.
            const borderStyle = a.fromGroup ? `1.5px dashed ${color}aa` : `1.5px solid ${color}`;
            const labelSuffix = a.fromGroup && a.group ? ` (${a.group})` : "";
            return a.avatar ? (
              <img key={a.user_id} src={a.avatar} alt={a.username} title={`${a.username}${labelSuffix}`}
                style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover", border: borderStyle, background: "#0d1120", marginLeft: i === 0 ? 0 : -6, position: "relative", zIndex: 20 - i }} />
            ) : (
              <span key={a.user_id} title={`${a.username}${labelSuffix}`}
                style={{ width: 22, height: 22, borderRadius: "50%", background: "#0d1120", border: borderStyle, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 9, color, marginLeft: i === 0 ? 0 : -6, position: "relative", zIndex: 20 - i }}>
                {(a.username || "?").charAt(0).toUpperCase()}
              </span>
            );
          })}
          {linkedAttendees.length > 8 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color, marginLeft: 4 }}>+{linkedAttendees.length - 8}</span>}
        </div>
      )}

      {/* Ship thumbnails */}
      {linkedShips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
          {linkedShips.slice(0, 4).map(s => (
            <div key={s.id} title={s.name}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 6px", background: "rgba(79,195,247,0.1)", border: "1px solid rgba(79,195,247,0.3)" }}>
              {s.image_url && <img src={s.image_url} alt={s.name} style={{ width: 18, height: 18, objectFit: "contain" }} />}
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 10, color: "#4fc3f7", whiteSpace: "nowrap", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
            </div>
          ))}
          {linkedShips.length > 4 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#4fc3f7", padding: "2px 6px" }}>+{linkedShips.length - 4}</span>}
        </div>
      )}

      {(() => {
        const blockFiles = (attachments || []).filter(a => a.node_id === node.id && (a.section_id === null || a.section_id === undefined));
        if (blockFiles.length === 0) return null;
        return (
          <div data-no-drag style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
            {blockFiles.slice(0, 3).map(f => <CardFilePreview key={f.id} file={f} color={color} />)}
            {blockFiles.length > 3 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: `${color}aa`, padding: "20px 6px 0" }}>+{blockFiles.length - 3}</span>}
          </div>
        );
      })()}

      {node.body && (
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.7)", whiteSpace: "pre-wrap", marginTop: 4, marginBottom: 8, paddingTop: 6, borderTop: `1px solid ${color}1a` }}>{node.body}</div>
      )}

      {/* Sub-section preview — renders the same kind of visual chips/avatars/
          ship cards the parent block does so sub-sections aren't summarised as
          bare "1 SHIP" counts. */}
      {sections.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4, paddingTop: 6, borderTop: `1px solid ${color}22` }}>
          {sections.slice(0, 4).map((s) => {
            // Build the crew list: explicit picks first (solid border), then any
            // members of the section's groups when show_group_members is on
            // (dashed border, tooltip shows source group).
            const explicitIds = new Set(s.attendee_user_ids || []);
            const explicitCrew = (s.attendee_user_ids || []).map(uid => {
              const a = attendeesWithProfile.find(p => p.user_id === uid);
              return a ? { ...a, fromGroup: false } : null;
            }).filter(Boolean);
            const groupSet = new Set(s.group_names || []);
            const groupCrew = s.show_group_members
              ? attendeesWithProfile.filter(a => a.group && groupSet.has(a.group) && !explicitIds.has(a.user_id)).map(a => ({ ...a, fromGroup: true }))
              : [];
            const sectionCrew = [...explicitCrew, ...groupCrew];
            const sectionShips = (s.ship_ids || []).map(sid => allShips.find(sh => sh.id === sid)).filter(Boolean);
            return (
              <div key={s.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", padding: "6px 8px" }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: "#e8eaf6", marginBottom: 4 }}>{s.title || "Untitled section"}</div>
                {(s.group_names?.length > 0) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 4 }}>
                    {s.group_names.map(g => (
                      <span key={g} style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, padding: "2px 5px", background: `${color}18`, border: `1px solid ${color}55`, color: `${color}cc`, textTransform: "uppercase" }}>{g}</span>
                    ))}
                  </div>
                )}
                {sectionCrew.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, marginBottom: 4 }}>
                    {sectionCrew.slice(0, 6).map((a, i) => {
                      const borderStyle = a.fromGroup ? `1.5px dashed ${color}aa` : `1.5px solid ${color}`;
                      const labelSuffix = a.fromGroup && a.group ? ` (${a.group})` : "";
                      return a.avatar ? (
                        <img key={a.user_id} src={a.avatar} alt={a.username} title={`${a.username}${labelSuffix}`}
                          style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover", border: borderStyle, background: "#0d1120", marginLeft: i === 0 ? 0 : -5, position: "relative", zIndex: 20 - i }} />
                      ) : (
                        <span key={a.user_id} title={`${a.username}${labelSuffix}`}
                          style={{ width: 18, height: 18, borderRadius: "50%", background: "#0d1120", border: borderStyle, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 8, color, marginLeft: i === 0 ? 0 : -5, position: "relative", zIndex: 20 - i }}>
                          {(a.username || "?").charAt(0).toUpperCase()}
                        </span>
                      );
                    })}
                    {sectionCrew.length > 6 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color, marginLeft: 4 }}>+{sectionCrew.length - 6}</span>}
                  </div>
                )}
                {sectionShips.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {sectionShips.slice(0, 4).map(sh => (
                      <div key={sh.id} title={sh.name}
                        style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 5px", background: "rgba(79,195,247,0.1)", border: "1px solid rgba(79,195,247,0.3)" }}>
                        {sh.image_url && <img src={sh.image_url} alt={sh.name} style={{ width: 16, height: 16, objectFit: "contain" }} />}
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 10, color: "#4fc3f7", whiteSpace: "nowrap", maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis" }}>{sh.name}</span>
                      </div>
                    ))}
                    {sectionShips.length > 4 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#4fc3f7", padding: "2px 5px" }}>+{sectionShips.length - 4}</span>}
                  </div>
                )}
                {(() => {
                  const sectionFiles = (attachments || []).filter(a => a.node_id === node.id && a.section_id === s.id);
                  if (sectionFiles.length === 0) return null;
                  return (
                    <div data-no-drag style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
                      {sectionFiles.slice(0, 3).map(f => <CardFilePreview key={f.id} file={f} color={color} compact />)}
                      {sectionFiles.length > 3 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: `${color}aa`, padding: "20px 5px 0" }}>+{sectionFiles.length - 3}</span>}
                    </div>
                  );
                })()}
              </div>
            );
          })}
          {sections.length > 4 && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>+{sections.length - 4} more sections</div>}
        </div>
      )}

      {/* Top-right action buttons */}
      <div data-no-drag style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 2 }}>
        <button onClick={onEdit} title="Edit phase"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.55)", fontFamily: "'Space Mono', monospace", fontSize: 9, padding: "2px 6px", cursor: "pointer", textTransform: "uppercase" }}>Edit</button>
        <button onClick={onDelete} title="Remove phase"
          style={{ background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "rgba(239,83,80,0.8)", fontSize: 10, padding: "2px 6px", cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>

      {/* Bottom-right resize handle */}
      <div data-no-drag onMouseDown={startResize} onTouchStart={startResize} title="Drag to resize"
        style={{ position: "absolute", bottom: 0, right: 0, width: 18, height: 18, cursor: "nwse-resize", background: `linear-gradient(135deg, transparent 50%, ${color}77 50%)` }} />

      {/* Anchor dots — 4 corners + 4 edge midpoints. Only shown when the
          "Connect Phases" toolbar toggle is active, since clicking the node
          body now starts a connection directly. Highlighted when this node
          is the active connect source. */}
      {isConnectMode && [
        { key: "tl", style: { top: 0, left: 0, transform: "translate(-50%, -50%)" } },
        { key: "tc", style: { top: 0, left: "50%", transform: "translate(-50%, -50%)" } },
        { key: "tr", style: { top: 0, right: 0, transform: "translate(50%, -50%)" } },
        { key: "rc", style: { top: "50%", right: 0, transform: "translate(50%, -50%)" } },
        { key: "br", style: { bottom: 0, right: 0, transform: "translate(50%, 50%)" } },
        { key: "bc", style: { bottom: 0, left: "50%", transform: "translate(-50%, 50%)" } },
        { key: "bl", style: { bottom: 0, left: 0, transform: "translate(-50%, 50%)" } },
        { key: "lc", style: { top: "50%", left: 0, transform: "translate(-50%, -50%)" } },
      ].map(({ key, style }) => (
        <div
          key={key}
          data-no-drag
          onMouseDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onConnectClick && onConnectClick(); }}
          title="Click to connect"
          style={{
            position: "absolute",
            width: 16, height: 16, borderRadius: "50%",
            background: isConnectSource ? "#4fc3f7" : "#0a0d18",
            border: `3px solid ${isConnectSource ? "#4fc3f7" : color}`,
            cursor: "crosshair",
            zIndex: 4,
            transition: "background 0.15s, transform 0.1s",
            ...style,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = isConnectSource ? "#4fc3f7" : `${color}66`; }}
          onMouseLeave={e => { e.currentTarget.style.background = isConnectSource ? "#4fc3f7" : "#0a0d18"; }}
        />
      ))}
    </div>
  );
}

// ─── Side-panel rich editor ────────────────────────────────────────────────
// Slides in from the right with the full set of fields plus sub-section
// management. Block-level fields update on every change; sub-sections are
// arrayed inside node.sections jsonb.
function NodeEditorPanel({ node, color, groups, attendees, ships, shipsByGroup = {}, flagshipObjs = [], attachments = [], onUpload, onDeleteAttachment, onClose, onChange }) {
  const sections = Array.isArray(node.sections) ? node.sections : [];

  // Compute the ship pool available for a particular set of group_names. If
  // the caller hasn't picked any groups, fall back to flagships only. Empty
  // result with no flagships → ShipPicker shows the "select a group first" hint.
  function shipsForGroups(groupNames) {
    const out = [];
    const seen = new Set();
    for (const g of (groupNames || [])) {
      for (const s of (shipsByGroup[g] || [])) {
        if (!seen.has(s.id)) { seen.add(s.id); out.push(s); }
      }
    }
    for (const s of flagshipObjs) {
      if (!seen.has(s.id)) { seen.add(s.id); out.push(s); }
    }
    return out;
  }
  const blockShipPool = shipsForGroups(node.group_names);
  // Set lookup so ShipPicker can highlight flagships visually.
  const flagshipIdSet = useMemo(() => new Set((flagshipObjs || []).map(s => s.id)), [flagshipObjs]);

  function patchSection(idx, patch) {
    const next = sections.map((s, i) => i === idx ? { ...s, ...patch } : s);
    onChange({ sections: next });
  }
  function addSection() {
    const next = [...sections, {
      id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      title: "New section",
      body: "",
      group_names: [],
      attendee_user_ids: [],
      ship_ids: [],
    }];
    onChange({ sections: next });
  }
  function removeSection(idx) {
    onChange({ sections: sections.filter((_, i) => i !== idx) });
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 12000, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(560px, 100vw)", height: "100vh", overflowY: "auto",
        background: "#0a0d18", borderLeft: `2px solid ${color}66`,
        boxShadow: `-20px 0 40px rgba(0,0,0,0.6)`,
        padding: "clamp(14px, 4vw, 24px) clamp(12px, 4vw, 22px)",
        boxSizing: "border-box",
        WebkitOverflowScrolling: "touch",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color, textTransform: "uppercase" }}>Edit Phase</div>
          <button onClick={onClose}
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
        </div>

        <FieldLabel color={color}>Title</FieldLabel>
        <input value={node.title || ""} onChange={e => onChange({ title: e.target.value })}
          style={{ width: "100%", padding: "8px 10px", background: "rgba(255,255,255,0.04)", border: `1px solid ${color}55`, color: "#e8eaf6", fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />

        <FieldLabel color={color}>Description</FieldLabel>
        <textarea value={node.body || ""} onChange={e => onChange({ body: e.target.value })} rows={4}
          placeholder="What happens during this phase?"
          style={{ width: "100%", padding: "8px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(232,234,246,0.85)", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, marginBottom: 16, boxSizing: "border-box", resize: "vertical" }} />

        <PickerBlock label="Groups assigned to this phase" color={color}>
          <GroupPicker value={node.group_names || []} options={groups} onChange={(v) => onChange({ group_names: v })} color={color} />
          {(node.group_names || []).length > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: node.show_group_members ? color : "rgba(232,234,246,0.55)", textTransform: "uppercase" }}>
              <input type="checkbox" checked={!!node.show_group_members}
                onChange={e => onChange({ show_group_members: e.target.checked })}
                style={{ accentColor: color }} />
              List all members of the group on this card
            </label>
          )}
        </PickerBlock>

        <PickerBlock label="Crew (event attendees)" color={color}>
          <AttendeePicker value={node.attendee_user_ids || []} attendees={attendees} onChange={(v) => onChange({ attendee_user_ids: v })} color={color} />
        </PickerBlock>

        <PickerBlock label="Ships" color={color}>
          <ShipPicker value={node.ship_ids || []} ships={blockShipPool}
            flagshipIds={flagshipIdSet}
            onChange={(v) => onChange({ ship_ids: v })}
            emptyHint={(node.group_names || []).length === 0
              ? (flagshipObjs.length > 0
                  ? "Pick a group to unlock its requested ships — flagships are still available regardless."
                  : "Select a group above first, or add a flagship to the event.")
              : "No ships are assigned to the selected group(s) yet. Add them on the event detail page."} />
        </PickerBlock>

        <PickerBlock label="Files attached to this phase" color={color}>
          <AttachmentUploader
            files={attachments.filter(a => a.node_id === node.id && (a.section_id === null || a.section_id === undefined))}
            color={color}
            onUpload={(file) => onUpload && onUpload({ file, nodeId: node.id, sectionId: null })}
            onDelete={onDeleteAttachment}
          />
        </PickerBlock>

        {/* Sub-sections */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${color}33` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <FieldLabel color={color} style={{ marginBottom: 0 }}>Sub-sections ({sections.length})</FieldLabel>
            <button onClick={addSection}
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "4px 10px", background: `${color}14`, border: `1px solid ${color}66`, color, textTransform: "uppercase", cursor: "pointer" }}>+ Section</button>
          </div>
          {sections.length === 0 && (
            <div style={{ padding: 16, border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center" }}>
              No sub-sections. Add one to break this phase into named sub-cards.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {sections.map((s, idx) => (
              <div key={s.id || idx} style={{ padding: 12, background: "rgba(255,255,255,0.02)", border: `1px solid ${color}33` }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input value={s.title || ""} placeholder="Section title"
                    onChange={e => patchSection(idx, { title: e.target.value })}
                    style={{ flex: 1, padding: "5px 8px", background: "rgba(255,255,255,0.04)", border: `1px solid ${color}44`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, boxSizing: "border-box" }} />
                  <button onClick={() => removeSection(idx)} title="Remove section"
                    style={{ background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "rgba(239,83,80,0.8)", fontSize: 11, padding: "0 8px", cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
                <textarea value={s.body || ""} placeholder="Section notes…" rows={2}
                  onChange={e => patchSection(idx, { body: e.target.value })}
                  style={{ width: "100%", padding: "5px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(232,234,246,0.8)", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, marginBottom: 8, boxSizing: "border-box", resize: "vertical" }} />
                <SubFieldLabel color={color}>Groups</SubFieldLabel>
                <GroupPicker value={s.group_names || []} options={groups} onChange={(v) => patchSection(idx, { group_names: v })} color={color} />
                {(s.group_names || []).length > 0 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.2, color: s.show_group_members ? color : "rgba(232,234,246,0.5)", textTransform: "uppercase" }}>
                    <input type="checkbox" checked={!!s.show_group_members}
                      onChange={ev => patchSection(idx, { show_group_members: ev.target.checked })}
                      style={{ accentColor: color }} />
                    List all members of the group on this section
                  </label>
                )}
                <SubFieldLabel color={color}>Crew</SubFieldLabel>
                <AttendeePicker value={s.attendee_user_ids || []} attendees={attendees} onChange={(v) => patchSection(idx, { attendee_user_ids: v })} color={color} />
                <SubFieldLabel color={color}>Ships</SubFieldLabel>
                <ShipPicker value={s.ship_ids || []}
                  ships={shipsForGroups((s.group_names && s.group_names.length > 0) ? s.group_names : (node.group_names || []))}
                  flagshipIds={flagshipIdSet}
                  onChange={(v) => patchSection(idx, { ship_ids: v })}
                  emptyHint={flagshipObjs.length > 0
                    ? "Pick a group to unlock its requested ships — flagships are still available regardless."
                    : "Pick a group on this section (or on the phase) — only ships assigned to that group show up."} />
                <SubFieldLabel color={color}>Files</SubFieldLabel>
                <AttachmentUploader
                  files={attachments.filter(a => a.node_id === node.id && a.section_id === s.id)}
                  color={color}
                  onUpload={(file) => onUpload && onUpload({ file, nodeId: node.id, sectionId: s.id })}
                  onDelete={onDeleteAttachment}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ color, children, style }) {
  return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.8, color: `${color}cc`, textTransform: "uppercase", marginBottom: 6, ...style }}>{children}</div>;
}
function SubFieldLabel({ color, children }) {
  return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", textTransform: "uppercase", marginTop: 8, marginBottom: 4 }}>{children}</div>;
}
function PickerBlock({ label, color, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <FieldLabel color={color}>{label}</FieldLabel>
      {children}
    </div>
  );
}

// ─── Pickers ──────────────────────────────────────────────────────────────
function GroupPicker({ value, options, onChange, color }) {
  function toggle(g) {
    if (value.includes(g)) onChange(value.filter(x => x !== g));
    else onChange([...value, g]);
  }
  if (!options || options.length === 0) {
    return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>No groups defined on this event yet.</div>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {options.map(g => {
        const on = value.includes(g);
        return (
          <button key={g} onClick={() => toggle(g)} type="button"
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, padding: "4px 8px", background: on ? `${color}22` : "transparent", border: `1px solid ${on ? color : "rgba(255,255,255,0.15)"}`, color: on ? color : "rgba(232,234,246,0.55)", textTransform: "uppercase", cursor: "pointer" }}>
            {on ? "✓ " : ""}{g}
          </button>
        );
      })}
    </div>
  );
}

function AttendeePicker({ value, attendees, onChange, color }) {
  // No search bar — every attendee is rendered as a clickable card. Optional
  // filter input is offered only when the list grows long.
  const [query, setQuery] = useState("");
  const set = new Set(value);
  const selected = attendees.filter(a => set.has(a.user_id));
  const eligible = attendees
    .filter(a => !set.has(a.user_id))
    .filter(a => !query || (a.username || "").toLowerCase().includes(query.toLowerCase()) || (a.group || "").toLowerCase().includes(query.toLowerCase()));

  if (attendees.length === 0) {
    return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1 }}>No attendees yet.</div>;
  }
  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {selected.map(a => (
            <span key={a.user_id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", background: "rgba(167,139,250,0.18)", border: "1px solid rgba(167,139,250,0.55)" }}>
              {a.avatar && <img src={a.avatar} alt={a.username} style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }} />}
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: "#e8eaf6" }}>{a.username}</span>
              <button onClick={() => onChange(value.filter(v => v !== a.user_id))} type="button"
                style={{ background: "transparent", border: "none", color: "rgba(239,83,80,0.7)", fontSize: 12, padding: 0, lineHeight: 1, cursor: "pointer" }}>×</button>
            </span>
          ))}
        </div>
      )}
      {attendees.length > 12 && (
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter…"
          style={{ width: "100%", padding: "5px 8px", background: "rgba(255,255,255,0.04)", border: `1px solid ${color}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
      )}
      {eligible.length === 0 ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1, padding: "6px 4px", textTransform: "uppercase" }}>
          {selected.length === attendees.length ? "All attendees added" : "No matches"}
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 220, overflowY: "auto", padding: 2 }}>
          {eligible.map(a => (
            <button key={a.user_id} type="button" onClick={() => onChange([...value, a.user_id])}
              title={a.group ? `${a.username} · ${a.group}` : a.username}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = `${color}14`; e.currentTarget.style.borderColor = `${color}66`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}>
              {a.avatar ? (
                <img src={a.avatar} alt={a.username} style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#0d1120", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 9, color }}>{(a.username || "?").charAt(0).toUpperCase()}</span>
              )}
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: "#e8eaf6" }}>{a.username}</span>
              {a.group && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.45)", letterSpacing: 1, textTransform: "uppercase" }}>{a.group}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Visual preview card for an attached file. Images render a larger thumbnail
// (clickable → in-place lightbox); PDFs get a distinct red badge. Click any
// non-image card to open the file in a new tab.
function FilePreviewCard({ file, kind, color, onPreview, onDelete }) {
  const url = file.file_url || "";
  const filename = file.filename || "Untitled";
  const baseStyle = {
    display: "inline-flex", flexDirection: "column", gap: 4,
    padding: 6, background: "rgba(255,255,255,0.03)",
    border: `1px solid ${kind === "pdf" ? "rgba(239,83,80,0.5)" : `${color}55`}`,
    width: 96, position: "relative", cursor: "pointer",
  };
  return (
    <div style={baseStyle}
      onClick={onPreview || (() => window.open(url, "_blank", "noopener,noreferrer"))}
      title={filename}>
      {kind === "image" ? (
        <div style={{ width: "100%", height: 64, background: "#05070f", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          <img src={url} alt={filename} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
        </div>
      ) : kind === "pdf" ? (
        <div style={{ width: "100%", height: 64, background: "rgba(239,83,80,0.08)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "1px solid rgba(239,83,80,0.25)" }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 18, letterSpacing: 1.5, color: "#ef5350" }}>PDF</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(239,83,80,0.7)", letterSpacing: 1 }}>↗ Open</div>
        </div>
      ) : (
        <div style={{ width: "100%", height: 64, background: "#05070f", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: `${color}cc` }}>FILE</div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>↗ Open</div>
        </div>
      )}
      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 10, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{filename}</div>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} type="button"
          title="Remove"
          style={{ position: "absolute", top: 2, right: 2, background: "rgba(10,13,24,0.85)", border: "1px solid rgba(239,83,80,0.4)", color: "#ef5350", fontSize: 11, lineHeight: 1, padding: "1px 5px", cursor: "pointer" }}>×</button>
      )}
    </div>
  );
}

// Click-to-close image lightbox. Image scales to fit within the viewport with
// a small caption underneath; ESC also closes.
function Lightbox({ file, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 13000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <img src={file.file_url} alt={file.filename}
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: "min(1200px, 95vw)", maxHeight: "82vh", objectFit: "contain", border: "1px solid rgba(255,255,255,0.1)" }} />
      <div onClick={e => e.stopPropagation()} style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.85)" }}>{file.filename}</div>
        <a href={file.file_url} target="_blank" rel="noreferrer"
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.6)", textTransform: "uppercase", textDecoration: "none" }}>Open in new tab ↗</a>
        <button onClick={onClose} type="button"
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.55)", textTransform: "uppercase", cursor: "pointer" }}>Close</button>
      </div>
    </div>
  );
}

// Tiny in-card file preview used on the phase canvas. Images get a 44x44
// thumbnail (clickable → lightbox); PDFs and other files get a labelled tile.
function CardFilePreview({ file, color, compact = false }) {
  const [lightbox, setLightbox] = useState(false);
  const type = (file.file_type || "").toLowerCase();
  const isImage = type.startsWith("image/");
  const isPdf = type.includes("pdf") || /\.pdf($|\?)/i.test(file.filename || "");
  const sz = compact ? 36 : 44;

  function open(e) {
    e.stopPropagation();
    if (isImage) setLightbox(true);
    else window.open(file.file_url, "_blank", "noopener,noreferrer");
  }

  return (
    <>
      <div onClick={open} title={file.filename}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: 2, background: "rgba(255,255,255,0.04)", border: `1px solid ${isPdf ? "rgba(239,83,80,0.5)" : `${color}55`}`, cursor: "pointer" }}>
        {isImage ? (
          <div style={{ width: sz, height: sz, background: "#05070f", overflow: "hidden" }}>
            <img src={file.file_url} alt={file.filename} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
          </div>
        ) : isPdf ? (
          <div style={{ width: sz, height: sz, background: "rgba(239,83,80,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: compact ? 11 : 13, letterSpacing: 1, color: "#ef5350" }}>PDF</span>
          </div>
        ) : (
          <div style={{ width: sz, height: sz, background: "#05070f", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: compact ? 9 : 11, color: `${color}cc` }}>FILE</span>
          </div>
        )}
      </div>
      {lightbox && <Lightbox file={file} onClose={() => setLightbox(false)} />}
    </>
  );
}

// Inline file upload + list. Supports both click-to-pick and drag-and-drop.
function AttachmentUploader({ files = [], color, onUpload, onDelete }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null); // open image preview
  const dragCounter = useRef(0); // dragenter/dragleave fire on every child; counter keeps us correct

  async function handleFiles(list) {
    if (!list || list.length === 0 || !onUpload) return;
    setUploading(true);
    for (const f of Array.from(list)) {
      await onUpload(f);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  function onDragEnter(e) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
      setDragOver(true);
    }
  }
  function onDragLeave(e) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  }
  function onDragOver(e) {
    // Browsers require dragover to be prevented for drop to fire.
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }
  function onDrop(e) {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0;
    setDragOver(false);
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }

  return (
    <div onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
      {files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {files.map(f => {
            const type = (f.file_type || "").toLowerCase();
            const isImage = type.startsWith("image/");
            const isPdf = type.includes("pdf") || /\.pdf($|\?)/i.test(f.filename || "");
            return (
              <FilePreviewCard
                key={f.id}
                file={f}
                kind={isImage ? "image" : isPdf ? "pdf" : "other"}
                color={color}
                onPreview={isImage ? () => setLightbox(f) : undefined}
                onDelete={onDelete ? () => onDelete(f.id) : undefined}
              />
            );
          })}
        </div>
      )}
      {lightbox && (
        <Lightbox file={lightbox} onClose={() => setLightbox(null)} />
      )}
      <label style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        padding: dragOver ? "12px 14px" : "8px 12px",
        border: `1px dashed ${dragOver ? color : `${color}66`}`,
        background: dragOver ? `${color}22` : `${color}08`,
        cursor: uploading ? "wait" : "pointer",
        fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2,
        color: dragOver ? color : `${color}cc`, textTransform: "uppercase",
        transition: "all 0.15s",
      }}>
        {uploading
          ? "Uploading…"
          : dragOver
            ? "Drop to upload"
            : "+ Attach File  ·  or drag & drop"}
        <input ref={inputRef} type="file" multiple disabled={uploading}
          onChange={e => handleFiles(e.target.files)}
          style={{ display: "none" }} />
      </label>
    </div>
  );
}

// ─── Comms Assignments side panel ───────────────────────────────────────
// Tabbed editor for VC (group → channel) and Radio (person → channel)
// assignments. Default tab is VC. Lives outside the node editor so it's
// reachable from the header at any time, regardless of which phase is open.
function CommsAssignmentsPanel({ color, comms, groups, attendees, onAdd, onUpdate, onDelete, onClose }) {
  const [tab, setTab] = useState("vc");
  const [draftGroup, setDraftGroup] = useState("");
  const [draftChannel, setDraftChannel] = useState("");
  const [draftUserId, setDraftUserId] = useState("");
  const [radioChannel, setRadioChannel] = useState("");

  const vcRows = useMemo(() => comms.filter(c => c.mode === "vc"), [comms]);
  const radioRows = useMemo(() => comms.filter(c => c.mode === "radio"), [comms]);

  async function addVc() {
    if (!draftGroup || !draftChannel.trim()) return;
    await onAdd({ mode: "vc", groupName: draftGroup, channel: draftChannel.trim() });
    setDraftGroup(""); setDraftChannel("");
  }
  async function addRadio() {
    if (!draftUserId || !radioChannel.trim()) return;
    await onAdd({ mode: "radio", userId: draftUserId, channel: radioChannel.trim() });
    setDraftUserId(""); setRadioChannel("");
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 12500, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(560px, 100vw)", height: "100vh", overflowY: "auto",
        background: "#0a0d18", borderLeft: `2px solid ${color}66`,
        boxShadow: `-20px 0 40px rgba(0,0,0,0.6)`,
        padding: "clamp(14px, 4vw, 24px) clamp(12px, 4vw, 22px)",
        boxSizing: "border-box",
        WebkitOverflowScrolling: "touch",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color, textTransform: "uppercase" }}>Comms Assignments</div>
          <button onClick={onClose} type="button"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.6)", fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, padding: "5px 10px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
        </div>

        {/* VC / Radio toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          {[{ id: "vc", label: "Voice Channels" }, { id: "radio", label: "Radio (Leaders)" }].map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              style={{ padding: "8px 14px", background: "transparent", border: "none", borderBottom: tab === t.id ? `2px solid ${color}` : "2px solid transparent", color: tab === t.id ? color : "rgba(232,234,246,0.5)", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer" }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "vc" ? (
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.5)", textTransform: "uppercase", marginBottom: 8 }}>
              Map each group to the voice channel they'll occupy.
            </div>

            {/* Existing rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
              {vcRows.length === 0 && (
                <div style={{ padding: 14, border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center" }}>
                  No VC assignments yet.
                </div>
              )}
              {vcRows.map(r => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: "rgba(255,255,255,0.02)", border: `1px solid ${color}33` }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, padding: "3px 8px", background: `${color}18`, border: `1px solid ${color}55`, color: `${color}cc`, textTransform: "uppercase", minWidth: 90, textAlign: "center" }}>{r.group_name || "—"}</span>
                  <span style={{ color: "rgba(232,234,246,0.3)", fontFamily: "'Space Mono', monospace", fontSize: 9 }}>→</span>
                  <input value={r.channel || ""} placeholder="Channel name…"
                    onChange={e => onUpdate(r.id, { channel: e.target.value })}
                    style={{ flex: 1, padding: "5px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12 }} />
                  <button onClick={() => onDelete(r.id)} type="button"
                    style={{ background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", fontSize: 11, padding: "3px 8px", cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>

            {/* New-row form */}
            <div style={{ padding: 10, border: `1px solid ${color}33`, background: "rgba(255,255,255,0.015)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: `${color}cc`, textTransform: "uppercase", marginBottom: 6 }}>Add Assignment</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <select value={draftGroup} onChange={e => setDraftGroup(e.target.value)}
                  style={{ flex: "1 1 140px", minWidth: 120, padding: "6px 8px", background: "#05070f", border: `1px solid ${color}44`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>
                  <option value="">— Group —</option>
                  {groups.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <input value={draftChannel} onChange={e => setDraftChannel(e.target.value)} placeholder="VC name…"
                  style={{ flex: "1 1 160px", minWidth: 140, padding: "6px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }} />
                <button onClick={addVc} type="button" disabled={!draftGroup || !draftChannel.trim()}
                  style={{ padding: "6px 12px", background: (!draftGroup || !draftChannel.trim()) ? "transparent" : `${color}22`, border: `1px solid ${color}`, color, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, cursor: (!draftGroup || !draftChannel.trim()) ? "not-allowed" : "pointer", textTransform: "uppercase", opacity: (!draftGroup || !draftChannel.trim()) ? 0.4 : 1 }}>+ Add</button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.5)", textTransform: "uppercase", marginBottom: 8 }}>
              Tag specific leaders / pilots and assign their radio channel.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
              {radioRows.length === 0 && (
                <div style={{ padding: 14, border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1.5, textTransform: "uppercase", textAlign: "center" }}>
                  No radio assignments yet.
                </div>
              )}
              {radioRows.map(r => {
                const att = attendees.find(a => a.user_id === r.user_id);
                return (
                  <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(167,139,250,0.4)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: "rgba(167,139,250,0.18)", border: "1px solid rgba(167,139,250,0.55)", minWidth: 0 }}>
                      {att?.avatar && <img src={att.avatar} alt={att.username} style={{ width: 14, height: 14, borderRadius: "50%", objectFit: "cover" }} />}
                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>{att?.username || "Unknown"}</span>
                    </span>
                    <span style={{ color: "rgba(232,234,246,0.3)", fontFamily: "'Space Mono', monospace", fontSize: 9 }}>→</span>
                    <input value={r.channel || ""} placeholder="Channel / frequency…"
                      onChange={e => onUpdate(r.id, { channel: e.target.value })}
                      style={{ flex: 1, padding: "5px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12 }} />
                    <button onClick={() => onDelete(r.id)} type="button"
                      style={{ background: "transparent", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", fontSize: 11, padding: "3px 8px", cursor: "pointer", lineHeight: 1 }}>×</button>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: 10, border: "1px solid rgba(167,139,250,0.33)", background: "rgba(255,255,255,0.015)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(167,139,250,0.85)", textTransform: "uppercase", marginBottom: 6 }}>Add Assignment</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <select value={draftUserId} onChange={e => setDraftUserId(e.target.value)}
                  style={{ flex: "1 1 160px", minWidth: 140, padding: "6px 8px", background: "#05070f", border: "1px solid rgba(167,139,250,0.5)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }}>
                  <option value="">— Person —</option>
                  {attendees.map(a => <option key={a.user_id} value={a.user_id}>{a.username}{a.group ? ` · ${a.group}` : ""}</option>)}
                </select>
                <input value={radioChannel} onChange={e => setRadioChannel(e.target.value)} placeholder="Radio channel / frequency…"
                  style={{ flex: "1 1 160px", minWidth: 140, padding: "6px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13 }} />
                <button onClick={addRadio} type="button" disabled={!draftUserId || !radioChannel.trim()}
                  style={{ padding: "6px 12px", background: (!draftUserId || !radioChannel.trim()) ? "transparent" : "rgba(167,139,250,0.18)", border: "1px solid #a78bfa", color: "#a78bfa", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, cursor: (!draftUserId || !radioChannel.trim()) ? "not-allowed" : "pointer", textTransform: "uppercase", opacity: (!draftUserId || !radioChannel.trim()) ? 0.4 : 1 }}>+ Add</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShipPicker({ value, ships, onChange, emptyHint, flagshipIds }) {
  // All ships render as a clickable card grid. Filter input shows only when
  // the fleet is large enough that scrolling alone is unfriendly.
  const [query, setQuery] = useState("");
  const set = new Set(value);
  const selected = ships.filter(s => set.has(s.id));
  const eligible = ships
    .filter(s => !set.has(s.id))
    .filter(s => !query || (s.name || "").toLowerCase().includes(query.toLowerCase()) || (s.manufacturer || "").toLowerCase().includes(query.toLowerCase()));

  // If the pool is empty AND nothing is selected, render the contextual hint
  // (e.g. "select a group first") instead of "All ships added".
  if (ships.length === 0 && selected.length === 0 && emptyHint) {
    return <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", letterSpacing: 1, padding: "8px 4px", textTransform: "uppercase", lineHeight: 1.5 }}>{emptyHint}</div>;
  }

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {selected.map(s => {
            const isFlag = flagshipIds && flagshipIds.has(s.id);
            return (
              <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", background: isFlag ? "rgba(252,214,87,0.15)" : "rgba(79,195,247,0.15)", border: `1px solid ${isFlag ? "rgba(252,214,87,0.5)" : "rgba(79,195,247,0.4)"}` }}>
                {s.image_url && <img src={s.image_url} alt={s.name} style={{ width: 22, height: 22, objectFit: "contain" }} />}
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: isFlag ? "#fcd657" : "#4fc3f7" }}>{s.name}</span>
                {isFlag && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#fcd657", letterSpacing: 1, textTransform: "uppercase" }}>★ Flag</span>}
                <button onClick={() => onChange(value.filter(v => v !== s.id))} type="button"
                  style={{ background: "transparent", border: "none", color: "rgba(239,83,80,0.7)", fontSize: 12, padding: 0, lineHeight: 1, cursor: "pointer" }}>×</button>
              </span>
            );
          })}
        </div>
      )}
      {ships.length > 24 && (
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Filter ships…"
          style={{ width: "100%", padding: "5px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(79,195,247,0.33)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
      )}
      {eligible.length === 0 ? (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1, padding: "6px 4px", textTransform: "uppercase" }}>
          {selected.length === ships.length ? "All ships added" : "No matches"}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6, maxHeight: 260, overflowY: "auto", padding: 2 }}>
          {eligible.map(s => {
            const isFlag = flagshipIds && flagshipIds.has(s.id);
            return (
              <button key={s.id} type="button" onClick={() => onChange([...value, s.id])}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: isFlag ? "rgba(252,214,87,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${isFlag ? "rgba(252,214,87,0.4)" : "rgba(255,255,255,0.1)"}`, cursor: "pointer", textAlign: "left", minWidth: 0, transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = isFlag ? "rgba(252,214,87,0.15)" : "rgba(79,195,247,0.1)"; e.currentTarget.style.borderColor = isFlag ? "rgba(252,214,87,0.7)" : "rgba(79,195,247,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = isFlag ? "rgba(252,214,87,0.08)" : "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = isFlag ? "rgba(252,214,87,0.4)" : "rgba(255,255,255,0.1)"; }}>
                {s.image_url && <img src={s.image_url} alt={s.name} style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 11, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden" }}>
                    {isFlag && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#fcd657", letterSpacing: 1, textTransform: "uppercase", flexShrink: 0 }}>★ Flag</span>}
                    {s.manufacturer && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.manufacturer}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
